import { gql } from "@apollo/client";
import { client } from "../apolloClient";
import { useQuery, useMutation } from "@apollo/client";

/**
 * useSkillApi custom hook for skill operations.
 * - Provides CRUD operations for skills using Apollo Client and GraphQL.
 * - Handles cache updates for skill queries and mutations.
 * - Exposes helpers for reading/writing/updating skills in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting skills.
 * - Supports fetching by skill ID.
 * - Handles optimistic UI updates and error logging.
 */

// GraphQL Queries and Mutations
const SKILLS_QUERIES = {
    GET_SKILLS: gql`
    query GetSkills {
  GetSkills {
    skill_id
    skill_cat_id
    skill_name
    skill_description
    is_active
    created_on
    updated_on
  }
}
  `,

    ADD_SKILL: gql`
    mutation CreateSkill($skillCatId: Int!, $skillName: String!, $skillDescription: String!) {
  CreateSkill(skill_cat_id: $skillCatId, skill_name: $skillName, skill_description: $skillDescription) {
    skill_id
    skill_cat_id
    skill_name
    skill_description
    is_active
    created_on
    updated_on
  }
}
  `,

    UPDATE_SKILL: gql`
  mutation UpdateSkill($skillId: ID!, $skillName: String, $skillDescription: String, $skillCatId: Int) {
  UpdateSkill(skill_id: $skillId, skill_name: $skillName, skill_description: $skillDescription, skill_cat_id: $skillCatId) {
    skill_id
    skill_cat_id
    skill_name
    skill_description
    is_active
    created_on
    updated_on
  }
}
  `,

    DELETE_SKILL: gql`
   mutation DeleteSkill($skillId: ID!) {
  DeleteSkill(skill_id: $skillId) {
    skill_id
    skill_cat_id
    skill_name
    skill_description
    is_active
    created_on
    updated_on
  }
}
  `,
};

// Cache operations
const cacheOperations = {
    /**
     * Writes skills to Apollo cache.
     * @param {Array} skills
     */
    writeSkills: (skills) => {
        if (!skills) return;
        client.writeQuery({
            query: SKILLS_QUERIES.GET_SKILLS,
            data: {
                GetSkills: skills,
            },
        });
    },

    /**
     * Reads skills from Apollo cache.
     * @returns {Array}
     */
    readSkills: () => {
        try {
            const { GetSkills } =
                client.readQuery({
                    query: SKILLS_QUERIES.GET_SKILLS,
                }) || {};
            return GetSkills || [];
        } catch {
            return [];
        }
    },

    /**
     * Updates a skill in Apollo cache.
     * @param {Object} updatedSkill
     */
    updateSkillsInCache: (updatedSkill) => {
        const existingSkills = cacheOperations.readSkills();
        const updatedSkills = existingSkills.map((skill) =>
            skill.skill_id === updatedSkill.skill_id ? updatedSkill : skill
        );
        cacheOperations.writeSkills(updatedSkills);
    },
};

export const useSkillApi = () => {
    const [addSkillMutation] = useMutation(SKILLS_QUERIES.ADD_SKILL);
    const [updateSkillMutation] = useMutation(SKILLS_QUERIES.UPDATE_SKILL);

    /**
     * Fetches all skills.
     * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
     */
    const getSkills = async () => {
        try {
            const { data } = await client.query({
                query: SKILLS_QUERIES.GET_SKILLS,
                fetchPolicy: "network-only",
            });
            return { success: true, data: data.GetSkills };
        } catch (error) {
            console.error("Error fetching skills:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Fetches a skill by its ID.
     * @param {number|string} skill_id
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const getSkillById = async (skill_id) => {
        try {
            const { data } = await client.query({
                query: SKILLS_QUERIES.GET_SKILL_BY_ID,
                variables: { skill_id },
                fetchPolicy: "network-only",
            });
            return { success: true, data: data.GetSkillById };
        } catch (error) {
            console.error("Error fetching skill:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Creates a new skill.
     * @param {Object} formData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const createSkill = async (formData) => {
        try {

            const { data, errors } = await addSkillMutation({
                variables: {
                    skillName: formData.skill_name,
                    skillCatId: parseInt(formData.skill_cat_id),
                    skillDescription: formData.skill_description,
                    isActive: formData.is_active,
                },
            });

            if (errors?.length > 0) {
                throw new Error(errors[0].message);
            }

            if (!data?.CreateSkill) {
                throw new Error("Failed to create skill");
            }

            return {
                success: true,
                data: data.CreateSkill,
            };
        } catch (error) {
            // Handle GraphQL errors
            if (error.graphQLErrors?.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                throw new Error(graphQLError.message);
            }

            // Handle network errors
            if (error.networkError?.result?.errors?.length > 0) {
                const networkError = error.networkError.result.errors[0];
                throw new Error(networkError.message);
            }

            throw error;
        }
    };

    /**
     * Updates an existing skill.
     * @param {number|string} skillId
     * @param {Object} formData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const updateSkill = async (skillId, formData) => {
        try {
            const { data, errors } = await updateSkillMutation({
                variables: {
                    skillId: parseInt(skillId),
                    skillName: formData.skill_name,
                    skillCatId: parseInt(formData.skill_cat_id),
                    skillDescription: formData.skill_description,
                    isActive: formData.is_active,
                },
            });

            if (errors?.length > 0) {
                throw new Error(errors[0].message);
            }

            if (!data?.UpdateSkill) {
                throw new Error("Failed to update skill");
            }

            return {
                success: true,
                data: data.UpdateSkill,
            };
        } catch (error) {
            // Handle GraphQL errors
            if (error.graphQLErrors?.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                throw new Error(graphQLError.message);
            }

            // Handle network errors
            if (error.networkError?.result?.errors?.length > 0) {
                const networkError = error.networkError.result.errors[0];
                throw new Error(networkError.message);
            }

            throw error;
        }
    };

    /**
     * Deletes a skill by its ID.
     * @param {number|string} skill_id
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const deleteSkill = async (skill_id) => {
        try {
            const { data } = await client.mutate({
                mutation: SKILLS_QUERIES.DELETE_SKILL,
                variables: { skillId: skill_id },
            });

            const existingSkills = cacheOperations.readSkills();
            cacheOperations.writeSkills(
                existingSkills.filter((skill) => skill.skill_id !== skill_id)
            );

            return { success: true, data: data.DeleteClient };
        } catch (error) {
            console.error("Error deleting skill:", error);
            return { success: false, error: error.message };
        }
    };

    return {
        getSkills,
        getSkillById,
        createSkill,
        updateSkill,
        deleteSkill,
        cacheOperations,
    };
};
